import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import {
  Users,
  CheckCircle,
  Flag,
  CreditCard,
  FileEdit,
  BarChart3,
  Receipt,
  LogOut,
  X,
} from "lucide-react";
import { useDispatch } from "react-redux";
import { logout } from "../../redux/slice";
import "../../styles/layout.css";
import { useLocation } from "react-router-dom";

// Import your logo - adjust the path based on your file structure
import prosperousTechLogo from "../../assets/prosperousTech.png"; // or .svg if you have SVG
import { useTranslation } from "react-i18next";

const Sidebar = ({ activeSection, setActiveSection }) => {
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();

  // Auto-update date/time
  useEffect(() => {
    const timer = setInterval(() => setCurrentDateTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) =>
    date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

  const formatDate = (date) =>
    date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

  const handleLogout = () => {
    setShowLogoutModal(false);
    dispatch(
      logout({
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
        error: null,
        sessionChecked: false,
      })
    );
  };

const { t } = useTranslation();

  const sidebarItems = [
    { id: "dashboard", label: t("sidebar.dashboard"), icon: BarChart3 },
    { id: "users", label: t("sidebar.users"), icon: Users },
    { id: "approvals", label: t("sidebar.approvals"), icon: CheckCircle },
    { id: "reports", label: t("sidebar.reports"), icon: Flag },
    { id: "subscriptions", label: t("sidebar.subscriptions"), icon: CreditCard },
    { id: "payments", label: t("sidebar.payments"), icon: Receipt },
    { id: "cms", label: t("sidebar.cms"), icon: FileEdit }
  ];

  const getActiveSectionForSidebar = () => {
    if (location.pathname.includes("/admin/user-profile/")) {
      return "users";
    }
    return activeSection;
  };

  const currentActiveSection = getActiveSectionForSidebar();

  return (
    <>
      <div className="admin-sidebar-fixed">
        {/* Navigation */}
        <nav className="admin-sidebar-nav">
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            return (
              <Button
                key={item.id}
                variant={currentActiveSection === item.id ? "default" : "ghost"}
                className="admin-sidebar-button"
                onClick={() => setActiveSection(item.id)}
              >
                <div className="flex items-center gap-3 w-full cursor-pointer">
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm font-medium cursor-pointer">
                    {item.label}
                  </span>
                </div>
              </Button>
            );
          })}
        </nav>

        {/* Footer with time + Logout + Powered by */}
        <div className="admin-sidebar-footer">
          <div className="admin-datetime-display">
            <div className="admin-datetime-text font-mono">
              {formatTime(currentDateTime)} {formatDate(currentDateTime)}
            </div>

            {/* Logout button */}
            <span
              variant="outline"
              size="sm"
              className="admin-logout-button"
              onClick={() => setShowLogoutModal(true)}
            >
              <LogOut className="h-5 w-5" /> Logout
            </span>
            <div className="admin-divider-line"></div>

            {/* Powered by section with logo and text in same line */}
            <div className="admin-powered-by"
            style={{paddingBottom:"10px"}}
            >
              <div className="flex items-center justify-start gap-2">
                <p className="admin-powered-text text-xs font-medium whitespace-nowrap">
                  Powered by
                </p>
                <img 
                  src={prosperousTechLogo} 
                  alt="ProsperousTech Logo" 
                  style={{ height: '30px' }}
                  className="w-auto opacity-30"
                  onError={(e) => {
                    console.error('Failed to load logo:', prosperousTechLogo);
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Logout Confirm Modal */}
      {showLogoutModal && (
        <div className="properous-logout-modal-overlay">
          <div className="properous-logout-modal-container">
            <div className="properous-logout-modal-content">
              {/* Header */}
              <div className="properous-logout-modal-header">
                <h2 className="properous-logout-modal-title">
                  Confirm Logout
                </h2>
                <button
                  onClick={() => setShowLogoutModal(false)}
                  className="properous-logout-modal-close-btn"
                  aria-label="Close logout confirmation"
                >
                  <X className="properous-logout-modal-close-icon" />
                </button>
              </div>

              {/* Body */}
              <div className="properous-logout-modal-body">
                <div className="properous-logout-modal-icon-wrapper">
                  <LogOut className="properous-logout-modal-icon" />
                </div>
                <p className="properous-logout-modal-message">
                  Are you sure you want to log out from your admin account?
                </p>
                <p className="properous-logout-modal-submessage">
                  You will need to log in again to access the admin panel.
                </p>
              </div>

              {/* Footer */}
              <div className="properous-logout-modal-footer">
                <Button
                  variant="outline"
                  size="sm"
                  className="properous-logout-modal-cancel-btn"
                  onClick={() => setShowLogoutModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="properous-logout-modal-confirm-btn"
                  onClick={handleLogout}
                >
                  Yes, Logout
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;